// src/components/AddServiceModal.jsx
import React, { useEffect, useState } from 'react';
import { BsX, BsUpload } from 'react-icons/bs';
import { useServiceStore, useCategoryStore } from '../../store/serviceStore';
import axiosToken from '../../api/axiosClient';
import { toast } from 'react-toastify';
import { FiX } from 'react-icons/fi';

const AddServiceModal = ({ isAddModalOpen, closeAddModal }) => {
    const [isLoadingAddService, setIsLoadingAddService] = useState(false);
    const { loadServices, getServicesGroupByCategorie } = useServiceStore();
    const { allCategory, isLoadingCategory, getCategorie } = useCategoryStore();

    useEffect(() => {
        if (isAddModalOpen) {
            getCategorie();
        }
    }, [isAddModalOpen, getCategorie]);

    const [formData, setFormData] = useState({
        nom: '',
        description: '',
        price: '',
        categoryID: '',
        durationMinutes: '',
        image: null
    });

    const [imagePreview, setImagePreview] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Vérifier la taille du fichier (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast.error("L'image ne doit pas dépasser 5MB");
                return;
            }

            // Vérifier le type de fichier
            if (!file.type.startsWith('image/')) {
                toast.error("Veuillez sélectionner une image valide");
                return;
            }

            setFormData(prev => ({
                ...prev,
                image: file
            }));

            // Prévisualisation de l'image
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const resetForm = () => {
        setFormData({
            nom: '',
            description: '',
            price: '',
            categoryID: '',
            durationMinutes: '',
            image: null
        });
        setImagePreview(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation des champs requis
        if (!formData.nom.trim()) {
            toast.error("Le nom du service est requis");
            return;
        }

        if (!formData.price || parseFloat(formData.price) <= 0) {
            toast.error("Le prix doit être supérieur à 0");
            return;
        }

        if (!formData.categoryID) {
            toast.error("Veuillez sélectionner une catégorie");
            return;
        }

        try {
            setIsLoadingAddService(true);
            const formDataSending = new FormData();

            // Ajouter les champs au FormData
            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
                    formDataSending.append(key, formData[key]);
                }
            });

            const response = await axiosToken.post('/service/', formDataSending, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Recharger les données
            await Promise.all([
                loadServices(),
                getServicesGroupByCategorie()
            ]);

            toast.success("Service ajouté avec succès");
            resetForm();
            closeAddModal();

        } catch (error) {
            console.error("Erreur addService :", error);
            const errorMessage = error?.response?.data?.error ||
                error?.response?.data?.message ||
                "Erreur lors de l'ajout du service.";
            toast.error(errorMessage);
        } finally {
            setIsLoadingAddService(false);
        }
    };

    const handleClose = () => {
        resetForm();
        closeAddModal();
    };

    if (!isAddModalOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white ">
                {/* En-tête */}
                <div className="flex justify-between items-center py-3 px-6 border-b border-gray-200">
                    <h2 className="font-poppins font-semibold text-[18px] text-title">Ajouter un service</h2>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100  transition-colors"
                        disabled={isLoadingAddService}
                    >
                        <BsX size={24} />
                    </button>
                </div>

                {/* Formulaire */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">

                        {/* Upload d'image */}
                        <div className="">
                            <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                                Image du service
                            </label>
                            <div className="flex items-center gap-4">
                                {
                                    !imagePreview ?
                                        (<div className="flex-1">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="hidden"
                                                id="image-upload"
                                                disabled={isLoadingAddService}
                                            />
                                            <label
                                                htmlFor="image-upload"
                                                className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed  cursor-pointer transition-colors ${isLoadingAddService
                                                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                                                    : 'border-gray-300 hover:border-gray-400'
                                                    }`}
                                            >
                                                <BsUpload size={24} className={`mb-2 ${isLoadingAddService ? 'text-gray-300' : 'text-gray-400'}`} />
                                                <span className={`text-sm ${isLoadingAddService ? 'text-gray-300' : 'text-gray-500'} font-nunito text-[14px]`}>
                                                    Cliquez pour uploader une image
                                                </span>
                                                <span className="text-xs text-gray-400 mt-1 font-nunito text-[14px]">Max. 5MB</span>
                                            </label>
                                        </div>
                                        )
                                        :
                                        (
                                            <div className="flex-1 relative">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="w-full h-32 border-2 border-dashed object-fit "
                                                />
                                                {!isLoadingAddService && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, image: null }));
                                                            setImagePreview(null);
                                                        }}
                                                        className="absolute  top-0 right-0 bg-red-500 text-white rounded-full rounded-tr-lg w-6 h-6 flex items-center justify-center text-xs"
                                                    >
                                                        <FiX />
                                                    </button>
                                                )}
                                            </div>
                                        )
                                }
                            </div>
                        </div>

                        {/* Nom du service */}
                        <div>
                            <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                                Nom du service *
                            </label>
                            <input
                                type="text"
                                name="nom"
                                value={formData.nom}
                                onChange={handleInputChange}
                                required
                                disabled={isLoadingAddService}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-50 disabled:cursor-not-allowed font-nunito text-[14px] text-text"
                                placeholder="Entrez le nom du service"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={3}
                                disabled={isLoadingAddService}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-50 disabled:cursor-not-allowed font-nunito text-[14px] text-text"
                                placeholder="Description du service"
                            />
                        </div>

                        {/* Prix et Durée */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                                    Prix (F CFA) *
                                </label>
                                <input
                                    type="number"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleInputChange}
                                    required
                                    min="0"
                                    step="0.01"
                                    disabled={isLoadingAddService}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-50 disabled:cursor-not-allowed font-nunito text-[14px] text-text"
                                    placeholder="0.00"
                                />
                            </div>

                            <div>
                                <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                                    Durée (minutes)
                                </label>
                                <input
                                    type="number"
                                    name="durationMinutes"
                                    value={formData.durationMinutes}
                                    onChange={handleInputChange}
                                    min="0"
                                    disabled={isLoadingAddService}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-50 disabled:cursor-not-allowed font-nunito text-[14px] text-text"
                                    placeholder="Durée en minutes"
                                />
                            </div>
                        </div>

                        {/* Catégorie */}
                        <div>
                            <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2">
                                Catégorie *
                            </label>
                            {isLoadingCategory ? (
                                <div className="flex items-center gap-2 py-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-secondary-500"></div>
                                    <span className="text-secondary-500 text-sm font-nunito text-[14px]">Chargement des catégories...</span>
                                </div>
                            ) : (
                                <select
                                    name="categoryID"
                                    value={formData.categoryID}
                                    onChange={handleInputChange}
                                    required
                                    disabled={isLoadingAddService || isLoadingCategory}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-50 disabled:cursor-not-allowed font-nunito text-[14px] text-text"
                                >
                                    <option value="" disabled>Sélectionnez une catégorie</option>
                                    {allCategory?.map((category) => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                    </div>
                    {/* Boutons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isLoadingAddService}
                            className="px-6 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-nunito font-semibold text-[14px]"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={isLoadingAddService}
                            className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-nunito font-semibold text-[14px]"
                        >
                            {isLoadingAddService && (
                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            )}
                            {isLoadingAddService ? 'Ajout en cours...' : 'Ajouter le service'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddServiceModal;